/* eslint-disable @typescript-eslint/no-floating-promises */
/**
 * @author      hugo@zumper.com (Hugo Pineda)
 * @copyright   Copyright (c) 2023, Zumper
 */

import { Button, ButtonProps } from '@chakra-ui/react'
import { usePrevious } from '@zcode-ui/hook'
import {
  AnimationControls,
  LazyMotion,
  MotionConfig,
  domAnimation,
  m,
  useAnimation,
} from 'framer-motion'
import React, { useEffect } from 'react'

const topLineInitial = 'M 2.8 3.5 L 17.2 3.5'
const topLineExpnded = 'M 2.8 10 L 17.2 10'
const topLineCross = 'M 2.8 3 L 17.2 17'
const bottomLineInitial = 'M 2.8 16.5 L 17.2 16.5'
const bottomLineExpnded = 'M 2.8 10 L 17.2 10'
const bottomLineCross = 'M 2.8 17 L 17.2 3'

const topLineSequence = async (controls: AnimationControls, duration: number, expanded = false) => {
  await controls.start({
    d: topLineExpnded,
    transition: {
      duration: duration / 2,
    },
  })
  if (!expanded) {
    await controls.start({
      d: topLineInitial,
      transition: {
        duration: duration / 2,
      },
    })
  } else {
    await controls.start({
      d: topLineCross,
      transition: {
        duration: duration / 2,
      },
    })
  }
}

const bottomLineSequence = async (
  controls: AnimationControls,
  duration: number,
  expanded = false
) => {
  await controls.start({
    d: bottomLineExpnded,
    transition: {
      duration: duration / 2,
    },
  })
  if (!expanded) {
    await controls.start({
      d: bottomLineInitial,
      transition: {
        duration: duration / 2,
      },
    })
  } else {
    await controls.start({
      d: bottomLineCross,
      transition: {
        duration: duration / 2,
      },
    })
  }
}

export interface HamburgerCloseButtonProps extends ButtonProps {
  color?: string
  duration?: number
  expanded?: boolean
}

export const HamburgerCloseButton = ({
  color = '#000000',
  duration = 0.3,
  expanded = false,
  ...rest
}: HamburgerCloseButtonProps) => {
  const previousExpanded = usePrevious(expanded) ?? expanded
  const topLineControls = useAnimation()
  const bottomLineControls = useAnimation()

  useEffect(() => {
    if (previousExpanded !== expanded) {
      topLineSequence(topLineControls, duration, expanded)
      bottomLineSequence(bottomLineControls, duration, expanded)
    }
  }, [topLineControls, bottomLineControls, duration, expanded, previousExpanded])

  return (
    <LazyMotion features={domAnimation}>
      <Button
        _active={{ bg: 'gray.0' }}
        _hover={{ bg: 'gray.0' }}
        height={5}
        minWidth={5}
        px={0}
        py={0}
        variant="ghost"
        width={5}
        {...rest}
      >
        <MotionConfig
          transition={{
            ease: 'easeInOut',
            duration,
          }}
        >
          <svg height="20" viewBox="0 0 20 20" width="20">
            <m.path
              animate={topLineControls}
              fill="transparent"
              initial={{ d: expanded ? topLineCross : topLineInitial }}
              stroke={color}
              strokeLinecap="round"
              strokeWidth="1.5"
            />
            <m.path
              animate={{
                opacity: expanded ? 0 : 1,
              }}
              d="M 2.8 10 L 17.2 10"
              fill="transparent"
              initial={{ opacity: expanded ? 0 : 1 }}
              stroke={color}
              strokeLinecap="round"
              strokeWidth="1.5"
            />
            <m.path
              animate={bottomLineControls}
              fill="transparent"
              initial={{ d: expanded ? bottomLineCross : bottomLineInitial }}
              stroke={color}
              strokeLinecap="round"
              strokeWidth="1.5"
            />
          </svg>
        </MotionConfig>
      </Button>
    </LazyMotion>
  )
}
